import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  arrayUnion,
  arrayRemove,
} from '@firebase/firestore';
import { db } from './firebase';
import type { UserFavorites } from '../types';

const COLLECTION_NAME = 'userFavorites';

/**
 * Get user's favorites
 */
export async function getUserFavorites(userId: string): Promise<string[]> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return [];
  }

  const data = docSnap.data() as UserFavorites;
  return data.placeIds || [];
}

/**
 * Check if a place is a favorite
 */
export async function isFavorite(
  userId: string,
  placeId: string
): Promise<boolean> {
  const favorites = await getUserFavorites(userId);
  return favorites.includes(placeId);
}

/**
 * Toggle favorite status for a place
 * Returns true if place is now a favorite, false if removed
 */
export async function toggleFavorite(
  userId: string,
  placeId: string
): Promise<boolean> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    // Create new favorites document
    await setDoc(docRef, {
      userId,
      placeIds: [placeId],
      updatedAt: serverTimestamp(),
    });
    return true;
  }

  const data = docSnap.data() as UserFavorites;
  const isCurrentlyFavorite = data.placeIds?.includes(placeId) || false;

  if (isCurrentlyFavorite) {
    // Remove from favorites
    await setDoc(
      docRef,
      {
        placeIds: arrayRemove(placeId),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return false;
  } else {
    // Add to favorites
    await setDoc(
      docRef,
      {
        placeIds: arrayUnion(placeId),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return true;
  }
}

/**
 * Add a place to favorites
 */
export async function addFavorite(
  userId: string,
  placeId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    await setDoc(docRef, {
      userId,
      placeIds: [placeId],
      updatedAt: serverTimestamp(),
    });
  } else {
    await setDoc(
      docRef,
      {
        placeIds: arrayUnion(placeId),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }
}

/**
 * Remove a place from favorites
 */
export async function removeFavorite(
  userId: string,
  placeId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, userId);
  await setDoc(
    docRef,
    {
      placeIds: arrayRemove(placeId),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Subscribe to user's favorites (real-time)
 */
export function subscribeToFavorites(
  userId: string,
  callback: (placeIds: string[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const docRef = doc(db, COLLECTION_NAME, userId);

  return onSnapshot(
    docRef,
    (docSnap) => {
      if (!docSnap.exists()) {
        callback([]);
        return;
      }

      const data = docSnap.data() as UserFavorites;
      callback(data.placeIds || []);
    },
    (error) => {
      console.error('[FavoritesService] subscribeToFavorites error:', error);
      if (onError) {
        onError(error);
      }
      // Return empty array on permission error
      callback([]);
    }
  );
}
